import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { KeyRound, RefreshCw, Unplug } from "lucide-react";
import {
  disconnectRemoteLock,
  getRemoteLockErrorMessage,
  getRemoteLockOauthUrl,
  getRemoteLockStatus,
  isRemoteLockDisabledError,
  refreshRemoteLockInventory,
} from "../../utils/remotelock";

const PENDING_REMOTELOCK_OAUTH_KEY = "pending_remotelock_oauth";

function StatusPill({ status }) {
  const map = {
    connected: ["bg-green-50 text-green-700", "bg-green-500", "Connected"],
    needs_reauth: ["bg-amber-50 text-amber-700", "bg-amber-500", "Reconnect required"],
    pending: ["bg-gray-100 text-gray-600", "bg-gray-400", "Connecting…"],
    disconnected: ["bg-gray-100 text-gray-600", "bg-gray-400", "Disconnected"],
    not_connected: ["bg-gray-100 text-gray-600", "bg-gray-400", "Not connected"],
  };
  const [pill, dot, label] = map[status] || map.not_connected;
  return (
    <span className="flex items-center gap-2">
      <span className={`h-2 w-2 rounded-full ${dot}`} />
      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${pill}`}>{label}</span>
    </span>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-[#EAEAEA] bg-white px-3 py-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-[#888888]">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-[#111111]">{value}</p>
    </div>
  );
}

/**
 * Smart-lock connection card for /integrations. Rendered only while the
 * backend integration flag is on (a 503 REMOTELOCK_DISABLED hides the card).
 */
export default function RemoteLockConnectionPanel({ hostInfo }) {
  const hid = hostInfo?._id;
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!hid) return;
    setLoading(true);
    try {
      const data = await getRemoteLockStatus(hid);
      setStatus(data);
      setHidden(false);
    } catch (error) {
      if (isRemoteLockDisabledError(error)) {
        setHidden(true);
        return;
      }
      toast.error(getRemoteLockErrorMessage(error, "Failed to load RemoteLock status"));
    } finally {
      setLoading(false);
    }
  }, [hid]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConnect = async () => {
    setBusy("connect");
    try {
      const { oauthUrl } = await getRemoteLockOauthUrl(hid);
      if (!oauthUrl) throw new Error("No authorization URL returned");
      sessionStorage.setItem(PENDING_REMOTELOCK_OAUTH_KEY, JSON.stringify({ startedAt: Date.now() }));
      window.location.assign(oauthUrl);
    } catch (error) {
      toast.error(getRemoteLockErrorMessage(error, "Failed to start RemoteLock connection"));
      setBusy("");
    }
  };

  const handleRefresh = async () => {
    setBusy("refresh");
    try {
      const out = await refreshRemoteLockInventory(hid);
      toast.success(`Lock inventory refreshed: ${out?.devices ?? out?.deviceCount ?? "all"} devices${out?.truncated ? " (list truncated — contact support)" : ""}.`);
      await load();
    } catch (error) {
      toast.error(getRemoteLockErrorMessage(error, "Failed to refresh lock inventory"));
    } finally {
      setBusy("");
    }
  };

  const handleDisconnect = async () => {
    const active = status?.counts?.activeAccesses || 0;
    const ok = window.confirm(
      `Disconnect RemoteLock?\n\nAll door mappings will be suspended and ${active} active delivery credential${active === 1 ? "" : "s"} will be deactivated. Bellhopt concierges will fall back to your written entry instructions.`
    );
    if (!ok) return;
    setBusy("disconnect");
    try {
      const out = await disconnectRemoteLock(hid);
      toast.success(`RemoteLock disconnected. ${out?.accessesRevoked || 0} credential(s) deactivated.`);
      await load();
    } catch (error) {
      toast.error(getRemoteLockErrorMessage(error, "Failed to disconnect RemoteLock"));
    } finally {
      setBusy("");
    }
  };

  if (hidden || !hid) return null;

  const connected = status?.connected === true;
  const needsReauth = status?.status === "needs_reauth";

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 transition hover:border-gray-300">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
            <KeyRound size={19} />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 truncate">RemoteLock Smart Locks</h3>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <StatusPill status={status?.status || "not_connected"} />
              {connected && (
                <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-bold text-gray-700">
                  {status.counts?.mappings || 0} {status.counts?.mappings === 1 ? "door mapped" : "doors mapped"}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {connected ? (
            <>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={Boolean(busy) || loading}
                className="inline-flex items-center gap-1.5 rounded-xl bg-gray-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-800 disabled:opacity-50"
              >
                <RefreshCw size={14} className={busy === "refresh" ? "animate-spin" : ""} />
                Refresh locks
              </button>
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={Boolean(busy)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
              >
                <Unplug size={14} />
                Disconnect
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handleConnect}
              disabled={Boolean(busy) || loading}
              className="rounded-xl bg-gray-900 px-5 py-2 text-sm font-bold text-white transition hover:bg-gray-800 disabled:opacity-50"
            >
              {needsReauth ? "Reconnect" : "Connect"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-5">
        {connected ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Stat label="Account" value={status.accountEmail || "Linked"} />
              <Stat label="Locks found" value={status.counts?.devices ?? 0} />
              <Stat label="Active credentials" value={status.counts?.activeAccesses ?? 0} />
              <Stat label="Last lock sync" value={status.lastInventorySyncAt ? new Date(status.lastInventorySyncAt).toLocaleString() : "Pending…"} />
            </div>
            {status.inventoryTruncated && (
              <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Your RemoteLock account has more devices than we could list. Contact Bellhopt support before mapping doors.
              </p>
            )}
            {!status.webhooks?.configured && (
              <p className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                Lock event notifications are still being set up. This usually completes within a minute.
              </p>
            )}
            <p className="text-xs text-[#5F6368]">
              Map each property to its front door from the property page (<span className="font-semibold">Guest Access → Smart lock</span>). Bellhopt creates a temporary,
              single-delivery code for each stocking and deactivates it when the delivery is complete.{" "}
              <Link to="/properties" className="font-medium text-[#111111] hover:underline">
                Go to properties
              </Link>
            </p>
          </div>
        ) : (
          <p className="text-sm text-[#5F6368]">
            {needsReauth
              ? "Your RemoteLock authorization expired or was revoked. Reconnect to resume automatic delivery codes."
              : "Connect your RemoteLock account so Bellhopt can issue a temporary door code for each delivery and let your concierge unlock supported doors from the app. Bellhopt never sees or stores your personal lock codes."}
            {status?.lastError && needsReauth && <span className="mt-1 block text-xs text-amber-700">{status.lastError}</span>}
          </p>
        )}
      </div>
    </section>
  );
}
